// The editor keeps the user's choices in a `names` map keyed by the zone's editor LABEL (the stable
// per-session identity: the cluster id in relative mode, the class index in behavior mode) and
// stamps them onto the dissolved FC after every redissolve, so brush edits, k changes and undo
// never lose a typed name. THE DEFAULT IS DERIVED FROM NON-NAME PROPERTIES ONLY (`code`, else the
// zone number) — never from a `zone_name` already on the feature.

export type ZoneNames = ReadonlyMap<number, string>;

type Props = Record<string, unknown>;

/* * The name a zone shows with NO user choice: its class code (behavior/decile) or its number. */
export function defaultZoneName(p: Props | null | undefined): string {
  const code = p?.code;
  if (code != null && String(code).trim() !== "") return String(code);
  const z = p?.zone ?? p?.zone_number;
  return z == null ? "" : String(z);
}

/* * The name a zone shows: the user's, else the default. */
export function zoneNameFor(
  label: number,
  p: Props | null | undefined,
  names: ZoneNames,
): string {
  const chosen = names.get(label);
  return chosen != null && chosen !== "" ? chosen : defaultZoneName(p);
}

/* * A typed name, as stored: trimmed; empty means "back to the default". */
export function cleanZoneName(raw: string): string {
  return raw.replace(/\s+/g, " ").trim();
}

/*
 *
 * Stamp `zone_name` on every feature + `name` on every label point from the names map. Pure — new
 * arrays, the inputs untouched. Features whose `zone` is not a number are left as they are.
 */
export function stampZoneNames<L extends { zone: number }>(
  fc: GeoJSON.FeatureCollection | null,
  labelPoints: L[],
  names: ZoneNames,
): {
  fc: GeoJSON.FeatureCollection | null;
  labelPoints: (L & { name: string })[];
} {
  const stampedFc: GeoJSON.FeatureCollection | null = fc
    ? {
        ...fc,
        features: fc.features.map((f) => {
          const p = (f.properties ?? {}) as Props;
          if (typeof p.zone !== "number") return f;
          return {
            ...f,
            properties: { ...p, zone_name: zoneNameFor(p.zone, p, names) },
          };
        }),
      }
    : null;
  const byLabel = new Map<number, Props>();
  for (const f of fc?.features ?? []) {
    const p = (f.properties ?? {}) as Props;
    if (typeof p.zone === "number" && !byLabel.has(p.zone))
      byLabel.set(p.zone, p);
  }
  return {
    fc: stampedFc,
    labelPoints: labelPoints.map((lp) => ({
      ...lp,
      name: zoneNameFor(
        lp.zone,
        byLabel.get(lp.zone) ?? {
          zone: lp.zone,
          code: (lp as { code?: unknown }).code,
        },
        names,
      ),
    })),
  };
}

/*
 *
 * The names a PICKED/served zone layer already carries, keyed the way `labelsFromServedZones` labels
 * its cells (rank of `zone_number`/`zone`, 1-based; else feature order) — so a layer whose zones were
 * named "North"/"South" opens in Customize (Order Retest) still named that, not renumbered. Only names
 * that differ from the default are kept; a served AutoMap (`zone_name` = its number) seeds nothing.
 */
export function namesFromServedFc(
  fc: GeoJSON.FeatureCollection | null | undefined,
): Map<number, string> {
  const out = new Map<number, string>();
  const feats = fc?.features ?? [];
  const nOf = (f: GeoJSON.Feature): number => {
    const p = (f.properties ?? {}) as Props;
    return typeof p.zone_number === "number"
      ? p.zone_number
      : typeof p.zone === "number"
        ? p.zone
        : Number.NaN;
  };
  const nums = feats.map(nOf);
  const ranked = [...new Set(nums.filter((v) => Number.isFinite(v)))].sort(
    (a, b) => a - b,
  );
  feats.forEach((f, idx) => {
    const p = (f.properties ?? {}) as Props;
    const r = ranked.indexOf(nums[idx]!);
    const label = (r >= 0 ? r : idx) + 1;
    const name = p.zone_name;
    if (name == null || String(name).trim() === "") return;
    const s = String(name);
    if (s === String(label) || s === defaultZoneName({ ...p, zone: label }))
      return;
    out.set(label, s);
  });
  return out;
}

/* * Names two or more zones share, in the order first seen. */
export function duplicateZoneNames(named: { name: string }[]): string[] {
  const seen = new Set<string>();
  const dup: string[] = [];
  for (const { name } of named) {
    const k = name.trim().toLowerCase();
    if (!k) continue;
    if (seen.has(k)) {
      if (!dup.includes(name)) dup.push(name);
    } else seen.add(k);
  }
  return dup;
}
